import { ChangeDetectorRef, Component, computed, effect, input, OnDestroy, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Asset } from '@app/models/asset/asset';
import { WorkOrder } from '@app/models/order/work-order';
import { FinishingRouteProductionCard, Route } from '@app/models/production/finishing-route-production-card';
import { EnumValues } from '@card-software/library';
import { ProductionCard } from '@app/models/production/production-card';
import { WorkOrderProxyService } from '@app/proxies/order/work-order.proxy.service';
import { FinishingRouteProxyService } from '@app/proxies/production/finishing-route.proxy.service';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { EnumService } from '@app/services/enum.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';

@Component({
  selector: 'app-finishing-card',
  templateUrl: './finishing-card.component.html',
  styleUrl: './finishing-card.component.scss',
})
export class FinishingCardComponent implements OnInit, OnDestroy {
  asset = input<Asset | null>(null);
  finishingRouteProductionCard = input<FinishingRouteProductionCard | null>(null);
  showCompleteCard = input<boolean>(false);
  currentProductionCard = signal<ProductionCard | null>(null);
  currentWorkOrder = signal<WorkOrder | null>(null);

  // Enum values
  fabricFormEnums: EnumValues;

  // Timer state
  currentTime = signal<Date>(new Date());
  private timerInterval?: ReturnType<typeof setInterval>;

  // Computed properties for current active process (running)
  currentActiveProcess = computed<Route | null>(() => {
    const route = this.finishingRouteProductionCard();
    if (!route?.route || route.route.length === 0) return null;

    // Find the process that is currently running (has startTime but no endTime)
    const runningProcess = route.route.find((r) => {
      // A process is running if it has a startTime and no endTime
      const hasStartTime = r.startTime !== null && r.startTime !== undefined;
      const noEndTime = !r.endTime || r.endTime === null;
      return hasStartTime && noEndTime;
    });

    return runningProcess || null;
  });

  // Computed properties for current finished process (waiting for next action)
  currentFinishedProcess = computed<Route | null>(() => {
    const route = this.finishingRouteProductionCard();
    if (!route?.route || route.route.length === 0) return null;

    // Find the process that finished but hasn't been transferred yet
    return route.route.find((r) => r.endTime && !r.dateTimeAdded) || null;
  });

  // Computed elapsed time for running process (from startTime to now)
  processElapsedTime = computed<string>(() => {
    const process = this.currentActiveProcess();

    // Check if process exists and has a valid startTime
    if (!process || !process.startTime || process.startTime === null) {
      return '';
    }

    const start = new Date(process.startTime);
    const now = this.currentTime();

    // Ensure we don't show negative time
    if (start > now) {
      console.warn('Start time is in the future, returning 00:00:00');
      return '00:00:00';
    }

    return this.formatDuration(start, now);
  });

  // Computed finished time for completed process (from startTime to endTime)
  processFinishedTime = computed<string>(() => {
    const process = this.currentFinishedProcess();
    if (!process?.startTime || !process?.endTime) return '';

    const start = new Date(process.startTime);
    const end = new Date(process.endTime);

    return this.formatDuration(start, end);
  });

  showProcessTimer = computed<boolean>(() => {
    return !!this.currentActiveProcess()?.startTime;
  });

  showWaitingTimer = computed<boolean>(() => {
    const process = this.currentFinishedProcess();
    return !!process?.endTime && !process?.dateTimeAdded;
  });

  constructor(
    private router: Router,
    private productionCardProxyService: ProductionCardsProxyService,
    private workOrderProxyService: WorkOrderProxyService,
    private ctr: ChangeDetectorRef,
    public localeHelperService: LocaleHelperService,
    private finishingRouteProxyService: FinishingRouteProxyService,
    private enumService: EnumService,
  ) {
    this.fabricFormEnums = this.enumService.getEnum('FabricForm', 'itemEnums').values;
    effect(() => {
      const routeCard = this.finishingRouteProductionCard();
      if (routeCard) {
        this.getProductionCard(routeCard.productionCardReference);
      }
    });

    // Debug effect to track when active process changes
    effect(() => {
      const activeProcess = this.currentActiveProcess();
      const elapsedTime = this.processElapsedTime();

      console.log('=== Process Status ===');
      if (activeProcess) {
        console.log('Active process:', {
          processId: activeProcess.processId,
          processName: activeProcess.processName,
          startTime: activeProcess.startTime,
          endTime: activeProcess.endTime,
          elapsedTime: elapsedTime,
        });
      } else {
        console.log('No active process');
      }
      console.log('====================');
    });
  }

  ngOnInit(): void {
    // Start timer interval to update current time every second
    this.timerInterval = setInterval(() => {
      this.currentTime.set(new Date());
    }, 1000);
  }

  ngOnDestroy(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  redirectMachine(assetId: string) {
    this.router.navigate(['/finishing/dashboard', assetId]);
  }

  private getProductionCard(pcId: string) {
    this.productionCardProxyService.getProductionCardByID(pcId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.currentProductionCard.set(response.data);
          this.getWorkOrderByPC(response.data.workOrderReference!);
        }
      },
    });
  }

  private getWorkOrderByPC(workOrderId: string) {
    this.workOrderProxyService.getById(workOrderId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.currentWorkOrder.set(response.data);
          console.log(response);
          this.ctr.detectChanges();
        }
      },
      error: () => {
        console.error('Failed to get work order by id:', workOrderId);
      },
    });
  }

  public getBrand(workOrder: WorkOrder): string {
    if (workOrder) {
      return workOrder.brand ? workOrder.brand.name : 'N/A';
    }
    return 'N/A';
  }

  public getClient(workOrder: WorkOrder): string {
    if (workOrder) {
      return workOrder.customer ? workOrder.customer.name : 'N/A';
    }
    return 'N/A';
  }

  get clientPO(): string {
    const workOrder = this.currentWorkOrder();
    if (workOrder) {
      let clientPos = workOrder.fabrics
        .map((f) => f.garmetDetails?.map((g) => g.productOrderNumber))
        .flat()
        .filter((po) => po && po.trim() !== '');

      clientPos = Array.from(new Set(clientPos));

      return clientPos.length > 0 ? clientPos.join(', ') : 'N/A';
    }
    return 'N/A';
  }

  public getFabricFormLabel(fabricForm: number | undefined): string {
    if (fabricForm === undefined || fabricForm === null) {
      return '';
    }
    return this.fabricFormEnums[fabricForm] || '';
  }

  private formatDuration(start: Date, end: Date): string {
    const diffMs = end.getTime() - start.getTime();
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
}
